/**
 * The Brain read seam — web mirror of `Sources/Store/MemoriesStore.swift`.
 *
 * Mirrors the FactoryStore pattern: one interface the Brain tab reads through,
 * with a Mock implementation (offline sample data) and a live implementation
 * (`BrainMemoriesStore`) that hits the Worker's read-only memories REST API with
 * the caller's `brain_*` token. Backend selection follows the SAME switch the app
 * already uses: mock → MockMemoriesStore, supabase → BrainMemoriesStore.
 *
 * READ-ONLY. Memory writes stay on the MCP surface — no create/update/delete.
 */

import type { Memory, MemorySummary, MemoryType } from '../models/memory';

export interface MemoriesStore {
  /** GET /memories — workspace-scoped summaries, `updated_at` desc. */
  list(opts?: { workspace?: string; type?: MemoryType; limit?: number }): Promise<MemorySummary[]>;

  /** GET /memories/:id — one memory in full (content + frontmatter). */
  get(id: string): Promise<Memory>;

  /** GET /memories/search — keyword search over name/description/content. */
  search(opts: {
    query: string;
    workspace?: string;
    type?: MemoryType;
    limit?: number;
  }): Promise<MemorySummary[]>;
}

export class MemoriesStoreError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = 'MemoriesStoreError';
  }
}

/** Map the API's documented statuses to a friendly, user-facing line. */
export function friendlyMemoriesError(status: number, body: string): string {
  switch (status) {
    case 401:
      return 'Not authorized — your Brain session may have expired.';
    case 403:
      return "You don't have access to that workspace.";
    case 404:
      return 'That memory no longer exists.';
    default:
      return `Brain returned HTTP ${status}: ${body}`;
  }
}
